// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import React, { useCallback, MouseEvent } from 'react';
import { useNavigate, Link as RouterLink, LinkProps as RouterLinkProps, createPath } from 'react-router-dom';

const urlMapping: Record<string, string> = {
    '/plesk/admin-panel=server.tools/': '/admin/server/tools',
};

export const useNavigateCallback = () => {
    const navigate = useNavigate();

    return useCallback((e: MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
            return;
        }

        const url = (e.target as HTMLAnchorElement).closest('a')?.getAttribute('href');
        if (!url || !require('routes').isClientSideRedirectAllowed(url)) {
            return;
        }

        e.preventDefault();
        navigate(url);
    }, [navigate]);
};

type LinkProps = RouterLinkProps &{
    href?: string;
    disabled?: boolean;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    target?: string;
    rel?: string;
};

const Link = ({
    to,
    href,
    disabled,
    onClick,
    ...props
}: LinkProps) => {
    to = to ?? href;

    if (disabled) {
        to = '';
        onClick = undefined;
    }

    if (typeof to === 'string' && urlMapping[to]) {
        to = urlMapping[to];
    }

    if (typeof to === 'object' && to !== null) {
        to = createPath(to);
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (onClick) {
            onClick(event);
        }

        if (event.defaultPrevented) {
            return;
        }

        if (!to) {
            event.preventDefault();
            return;
        }

        if (!require('routes').isClientSideRedirectAllowed(to)) {
            event.preventDefault();

            const features: string[] = [];
            if (typeof props.rel === 'string' && props.rel.length > 0) {
                features.push(...props.rel.split(' '));
            }

            const target = event.ctrlKey || event.metaKey ? '_blank' : '_self';
            window.open(to, props.target || target, features.join(','));
        }
    };

    return <RouterLink to={to} onClick={handleClick} {...props} />;
};

export default Link;
